/*
  Display formatting. Wire values stay as ISO strings and raw numbers in the query cache;
  everything human-facing is produced here, once.

  The relative time formatter matters more than it looks. "Last Seen" is the column a
  technician scans to decide whether a machine is worth trying, and an absolute timestamp
  forces mental arithmetic under time pressure. So the table shows relative text with the
  absolute value in a title attribute and in a <time dateTime> for assistive technology,
  which gives both without widening the column.
*/

const relativeFormatter = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' });

const absoluteFormatter = new Intl.DateTimeFormat(undefined, {
  dateStyle: 'medium',
  timeStyle: 'short',
});

const timeOnlyFormatter = new Intl.DateTimeFormat(undefined, { timeStyle: 'short' });

const numberFormatter = new Intl.NumberFormat();

export function formatAbsolute(iso: string | null): string {
  if (iso === null) {
    return 'Never';
  }
  const ms = Date.parse(iso);
  return Number.isNaN(ms) ? 'Unknown' : absoluteFormatter.format(ms);
}

export function formatTimeOnly(iso: string | null): string {
  if (iso === null) {
    return '';
  }
  const ms = Date.parse(iso);
  return Number.isNaN(ms) ? '' : timeOnlyFormatter.format(ms);
}

const DIVISIONS: readonly { amount: number; unit: Intl.RelativeTimeFormatUnit }[] = [
  { amount: 60, unit: 'second' },
  { amount: 60, unit: 'minute' },
  { amount: 24, unit: 'hour' },
  { amount: 7, unit: 'day' },
  { amount: 4.34524, unit: 'week' },
  { amount: 12, unit: 'month' },
  { amount: Number.POSITIVE_INFINITY, unit: 'year' },
];

export function formatRelative(iso: string | null): string {
  if (iso === null) {
    return 'Never';
  }
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) {
    return 'Unknown';
  }
  let duration = (ms - Date.now()) / 1000;
  for (const division of DIVISIONS) {
    if (Math.abs(duration) < division.amount) {
      return relativeFormatter.format(Math.round(duration), division.unit);
    }
    duration /= division.amount;
  }
  return absoluteFormatter.format(ms);
}

/** Compact relative text for a dense table cell: "4m", "6d", "now". */
export function formatRelativeShort(iso: string | null): string {
  if (iso === null) {
    return 'Never';
  }
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) {
    return 'Unknown';
  }
  const seconds = Math.max(0, Math.round((Date.now() - ms) / 1000));
  if (seconds < 45) {
    return 'now';
  }
  if (seconds < 3600) {
    return `${String(Math.round(seconds / 60))}m`;
  }
  if (seconds < 86_400) {
    return `${String(Math.round(seconds / 3600))}h`;
  }
  if (seconds < 86_400 * 30) {
    return `${String(Math.round(seconds / 86_400))}d`;
  }
  return `${String(Math.round(seconds / (86_400 * 30)))}mo`;
}

export function formatNumber(value: number): string {
  return numberFormatter.format(value);
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) {
    return `${String(bytes)} B`;
  }
  const units = ['KB', 'MB', 'GB', 'TB'];
  let value = bytes / 1024;
  let unitIndex = 0;
  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }
  return `${value.toFixed(value < 10 ? 1 : 0)} ${units[unitIndex] ?? 'TB'}`;
}

export function formatDuration(startIso: string | null, endIso: string | null): string {
  if (startIso === null) {
    return '';
  }
  const start = Date.parse(startIso);
  const end = endIso === null ? Date.now() : Date.parse(endIso);
  if (Number.isNaN(start) || Number.isNaN(end)) {
    return '';
  }
  const totalSeconds = Math.max(0, Math.round((end - start) / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  if (hours > 0) {
    return `${String(hours)}h ${String(minutes)}m`;
  }
  if (minutes > 0) {
    return `${String(minutes)}m ${String(seconds)}s`;
  }
  return `${String(seconds)}s`;
}

/**
 * Human label for a dotted audit action: session.start becomes "Session start" and
 * device.fileTransfer becomes "Device file transfer". The camel case split lowercases the
 * second word, otherwise the label reads "Device file Transfer" with a stray capital.
 */
export function humaniseAction(action: string): string {
  const spaced = action
    .replace(/[._]/g, ' ')
    .replace(/([a-z])([A-Z])/g, (_match, before: string, after: string) => `${before} ${after.toLowerCase()}`);
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/** Initials for an avatar, at most two letters. */
export function initials(fullName: string): string {
  const parts = fullName.trim().split(/\s+/).filter((part) => part.length > 0);
  const first = parts[0]?.charAt(0) ?? '?';
  const last = parts.length > 1 ? (parts[parts.length - 1]?.charAt(0) ?? '') : '';
  return `${first}${last}`.toUpperCase();
}
